import { useCallback, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  ConsultantPortalContract,
  ConsultantPortalContractStatus,
} from '@alamo/types';

import { apiClient } from '../client';

const portalContractKey = (userId: string) => ['consultant-contract', 'portal', userId] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPortalStatus(value: unknown): value is ConsultantPortalContractStatus {
  if (!isRecord(value)) return false;
  if (
    !['required', 'signed', 'isBlocked', 'needsCompletion', 'canInitiate'].every(
      (key) => typeof value[key] === 'boolean',
    )
  )
    return false;
  if (
    value.nextStep !== null &&
    value.nextStep !== 'signature' &&
    value.nextStep !== 'registration'
  )
    return false;
  if (value.contract === null) return true;
  const contract = value.contract;
  if (
    !isRecord(contract) ||
    typeof contract.required !== 'boolean' ||
    (contract.status !== null && typeof contract.status !== 'string')
  )
    return false;
  if (
    contract.provider !== undefined &&
    contract.provider !== null &&
    contract.provider !== 'clicksign' &&
    contract.provider !== 'zapsign'
  )
    return false;
  if (contract.contractId !== undefined && typeof contract.contractId !== 'string') return false;
  return ['requestSignatureKey', 'providerEnvelopeUrl', 'expiresAt'].every(
    (key) =>
      contract[key] === undefined || contract[key] === null || typeof contract[key] === 'string',
  );
}

/** Concurrent callers share one operation until its transport settles. */
function useSingleFlight<T>() {
  const inFlight = useRef<Promise<T> | null>(null);
  return useCallback((operation: () => Promise<T>): Promise<T> => {
    if (inFlight.current) return inFlight.current;
    const request = operation();
    inFlight.current = request;
    const clear = () => {
      if (inFlight.current === request) inFlight.current = null;
    };
    void request.then(clear, clear);
    return request;
  }, []);
}

export function usePortalConsultantContract(userId: string, enabled = true) {
  return useQuery({
    queryKey: portalContractKey(userId),
    enabled: Boolean(userId) && enabled,
    queryFn: async ({ signal }) => {
      const response = await apiClient.get<ApiSuccess<unknown>>('/consultant-contract/status', {
        signal,
      });
      const value = response.data.data;
      if (!isPortalStatus(value)) throw new Error('Invalid consultant contract status response');
      return value;
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data?.isBlocked ? 5000 : 30000),
  });
}

export function useInitiatePortalConsultantContract(userId: string) {
  const client = useQueryClient();
  const singleFlight = useSingleFlight<ConsultantPortalContract>();
  return useMutation({
    mutationFn: () =>
      singleFlight(
        async () =>
          (
            await apiClient.post<ApiSuccess<ConsultantPortalContract>>(
              '/consultant-contract/initiate',
            )
          ).data.data,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: portalContractKey(userId) });
    },
  });
}

export function useCompletePortalConsultantContract() {
  const singleFlight = useSingleFlight<{ completed: boolean }>();
  return useMutation({
    mutationFn: () =>
      singleFlight(
        async () =>
          (
            await apiClient.post<ApiSuccess<{ completed: boolean }>>(
              '/consultant-contract/complete',
            )
          ).data.data,
      ),
  });
}
